import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DndContext, DragOverlay, closestCorners } from '@dnd-kit/core';
import { ListTodo, Plus } from 'lucide-react';


import { getBacklog } from '../../api/board';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Skeleton } from '../../components/Skeleton';
import { useHotkeys } from '../../hooks/useHotkeys';
import { useProject, useProjectMembers, useProjectPermissions } from '../../hooks/useProject';
import { humanizeError } from '../../lib/errors';
import { useProjectSocket } from '../../realtime/useProjectSocket';
import { BacklogSection } from './BacklogSection';
import { BulkActionsBar } from './BulkActionsBar';
import { BacklogDialogs } from './BacklogDialogs';
import { IssueCardView } from '../issue/IssueCardView';
import { IssuePanel } from '../issue/IssuePanel';
import { SprintSection } from './SprintSection';
import { useBacklogDnd } from './useBacklogDnd';
import { useBacklogSprints } from './useBacklogSprints';
import { collectIds, collectIssues } from './backlogModel';
import styles from './BacklogPage.module.css';

/** Сколько задач бэклога подгружаем за раз. */
const BACKLOG_PAGE_SIZE = 200;

/** `/projects/:key/backlog` — бэклог и спринты (docs/06 §3.3). */
export function BacklogPage() {
  const { key = '' } = useParams<{ key: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { project } = useProject(key);
  const { canManageProject } = useProjectPermissions(project);
  const members = useProjectMembers(key);

  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);
  const [lastClickedId, setLastClickedId] = useState<string | null>(null);
  const [createIssueSprintId, setCreateIssueSprintId] = useState<string | null>(null);
  const [isCreateIssueOpen, setCreateIssueOpen] = useState(false);

  const issueKey = searchParams.get('issue');

  // Порционный просмотр: большие бэклоги подгружаются частями, а не целиком.
  const [backlogLimit, setBacklogLimit] = useState(BACKLOG_PAGE_SIZE);

  const backlogQuery = useQuery({
    queryKey: queryKeys.backlog.page(key, backlogLimit),
    queryFn: ({ signal }) => getBacklog(key, { limit: backlogLimit, offset: 0 }, signal),
    enabled: key.length > 0,
    // Пока грузится следующая порция, показываем предыдущую — без «мигания».
    placeholderData: (previous) => previous,
  });

  const backlog = backlogQuery.data ?? null;
  const dnd = useBacklogDnd(key, backlog);

  // Другой проект — снова начинаем с первой порции.
  useEffect(() => {
    setBacklogLimit(BACKLOG_PAGE_SIZE);
  }, [key]);

  useProjectSocket({ projectKey: key.length > 0 ? key : null, onPresence: () => undefined });

  const patchParams = useCallback(
    (patch: Readonly<Record<string, string | null>>) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [name, value] of Object.entries(patch)) {
            if (value === null || value === '') {
              next.delete(name);
            } else {
              next.set(name, value);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useHotkeys(
    useMemo(
      () => ({
        g: () => patchParams({ issue: null }),
        escape: () => patchParams({ issue: null }),
      }),
      [patchParams],
    ),
  );

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.backlog.view(key) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.board.root(key) });
  }, [key, queryClient]);

  const sprints = useBacklogSprints(key, backlog?.sprintGroups ?? [], invalidate);

  const toggleSelect = useCallback(
    (issueId: string, event: { readonly shiftKey: boolean; readonly metaKey: boolean }) => {
      if (event.shiftKey && lastClickedId) {
        const all = collectIds(backlog);
        const from = all.indexOf(lastClickedId);
        const to = all.indexOf(issueId);
        if (from >= 0 && to >= 0) {
          const range = all.slice(Math.min(from, to), Math.max(from, to) + 1);
          setSelectedIds((previous) => [...new Set([...previous, ...range])]);
          return;
        }
      }
      setLastClickedId(issueId);
      setSelectedIds((previous) =>
        previous.includes(issueId) ? previous.filter((id) => id !== issueId) : [...previous, issueId],
      );
    },
    [backlog, lastClickedId],
  );

  const selectedIssues = useMemo(
    () => collectIssues(backlog).filter((issue) => selectedIds.includes(issue.id)),
    [backlog, selectedIds],
  );

  if (project && project.boardType !== 'scrum') {
    return (
      <div className={styles['page']}>
        <EmptyState
          icon={<ListTodo size={20} />}
          title="Бэклог доступен только для скрам-проектов"
          description="Смените тип доски в настройках проекта."
        />
      </div>
    );
  }

  return (
    <div className={styles['page']}>
      <header className={styles['header']}>
        <h1>Бэклог</h1>
        {canManageProject ? (
          <Button variant="secondary" icon={<Plus size={14} />} onClick={() => sprints.setCreateSprintOpen(true)}>
            Создать спринт
          </Button>
        ) : null}
      </header>

      {backlogQuery.isPending ? (
        <div className={styles['skeleton']}>
          <Skeleton variant="card" count={3} />
        </div>
      ) : backlogQuery.isError ? (
        <EmptyState
          icon={<ListTodo size={20} />}
          title="Не удалось загрузить бэклог"
          description={humanizeError(backlogQuery.error).message}
          action={
            <Button variant="primary" onClick={() => void backlogQuery.refetch()}>
              Повторить
            </Button>
          }
        />
      ) : !backlog ? null : (
        <DndContext
          sensors={dnd.sensors}
          collisionDetection={closestCorners}
          onDragStart={dnd.handleDragStart}
          onDragEnd={dnd.handleDragEnd}
          onDragCancel={dnd.handleDragCancel}
        >
          <div className={styles['content']}>
            {backlog.sprintGroups.map((group) => (
              <SprintSection
                key={group.sprint.id}
                sprint={group}
                canManage={canManageProject}
                isSelected={(id) => selectedIds.includes(id)}
                onOpenIssue={(value) => patchParams({ issue: value })}
                onToggleSelect={toggleSelect}
                onStart={() => sprints.startMutation.mutate(group.sprint.id)}
                onComplete={() => sprints.setCompleteTarget(group)}
                onDelete={() => sprints.setSprintToDelete(group)}
                onEdit={() => undefined}
                onCreateIssue={(sprintId) => {
                  setCreateIssueSprintId(sprintId);
                  setCreateIssueOpen(true);
                }}
              />
            ))}

            <BacklogSection
              issues={backlog.backlog}
              canManage={canManageProject}
              isSelected={(id) => selectedIds.includes(id)}
              onOpenIssue={(value) => patchParams({ issue: value })}
              onToggleSelect={toggleSelect}
            />

            {backlog.hasMore ? (
              <div className={styles['more']}>
                <span className={styles['moreHint']}>
                  Показано {backlog.backlog.length} из {backlog.total}
                </span>
                <Button
                  variant="secondary"
                  onClick={() => setBacklogLimit((current) => current + BACKLOG_PAGE_SIZE)}
                  disabled={backlogQuery.isFetching}
                >
                  {backlogQuery.isFetching ? 'Загружаем…' : 'Показать ещё'}
                </Button>
              </div>
            ) : null}
          </div>

          <DragOverlay dropAnimation={null}>
            {dnd.activeCard ? (
              <div className={styles['overlay']}>
                <IssueCardView issue={dnd.activeCard} />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      <BulkActionsBar
        selected={selectedIssues}
        members={members}
        sprints={sprints.futureSprints}
        onClear={() => setSelectedIds([])}
        onAssign={(assigneeId) => sprints.bulkMutation.mutate({ issueIds: [...selectedIds], patch: { assigneeId } })}
        onPriority={(priority) => sprints.bulkMutation.mutate({ issueIds: [...selectedIds], patch: { priority } })}
        onMoveToSprint={(sprintId) => sprints.bulkMutation.mutate({ issueIds: [...selectedIds], patch: { sprintId } })}
        onDelete={() => sprints.bulkMutation.mutate({ issueIds: [...selectedIds], patch: { assigneeId: null } })}
      />

      <BacklogDialogs
        completeTarget={sprints.completeTarget}
        futureSprints={sprints.futureSprints}
        isCompletePending={sprints.completeMutation.isPending}
        completeError={sprints.completeMutation.isError ? humanizeError(sprints.completeMutation.error).message : null}
        onCloseComplete={() => sprints.setCompleteTarget(null)}
        onConfirmComplete={(body) => {
          if (sprints.completeTarget) {
            sprints.completeMutation.mutate({ sprintId: sprints.completeTarget.sprint.id, body });
          }
        }}
        sprintToDelete={sprints.sprintToDelete}
        isDeletePending={sprints.deleteSprintMutation.isPending}
        onCloseDelete={() => sprints.setSprintToDelete(null)}
        onConfirmDelete={() => {
          if (sprints.sprintToDelete) {
            sprints.deleteSprintMutation.mutate(sprints.sprintToDelete.sprint.id);
          }
        }}
        isCreateOpen={sprints.isCreateSprintOpen}
        newSprintName={sprints.newSprintName}
        isCreatePending={sprints.createSprintMutation.isPending}
        onNewSprintNameChange={sprints.setNewSprintName}
        onCloseCreate={() => sprints.setCreateSprintOpen(false)}
        onCreateSprint={() => sprints.createSprintMutation.mutate(sprints.newSprintName.trim())}
        createIssueOpen={isCreateIssueOpen}
        createIssueSprintId={createIssueSprintId}
        onCloseCreateIssue={() => {
          setCreateIssueOpen(false);
          setCreateIssueSprintId(null);
        }}
      />

      <IssuePanel issueKey={issueKey} projectKey={key} onClose={() => patchParams({ issue: null })} />
    </div>
  );
}
